import React from 'react';
import { useGameStore } from '../../state/gameStore';
import { Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { customProblemEditor } from '../../../game/custom-editor';
import type { RationalProblem } from '../../../core/rational';
import { formatFractionForDisplay } from '../../../core/fraction';

// Simple HTML-based formula renderer
const SimpleMathFormula: React.FC<{
  terms: Array<{ coeff: number, varName: string, displayCoeff?: string }>,
  operator?: string,
  rhs?: number | string,
  prefix?: string
}> = ({ terms, operator, rhs, prefix }) => {

  return (
    <div className="text-white text-lg font-mono my-1 whitespace-nowrap overflow-x-auto custom-scrollbar font-bold">
      {prefix && <span className="font-bold mr-2 text-sky-400">{prefix}</span>}

      {terms.map((term, i) => {
        const { coeff, varName, displayCoeff } = term;
        const absCoeff = Math.abs(coeff);

        // Handle sign
        let signStr = '';
        if (i === 0) {
          if (coeff < 0) signStr = '-';
        } else {
          signStr = coeff < 0 ? ' - ' : ' + ';
        }

        // Handle coefficient display
        // If coeff is 1 or -1, don't show "1" unless variable is empty (constant term)
        const showCoeff = absCoeff !== 1 || varName === '';
        const coeffStr = showCoeff ? (displayCoeff ?? absCoeff) : '';

        // Handle variable subscript (e.g. x1 -> x₁ using sub tag if needed, or just plain text)
        // For simplicity in font-mono, x1 is fine.

        return (
          <span key={i}>
            {signStr}
            {coeffStr}
            <span className="font-bold italic text-white">{varName}</span>
          </span>
        );
      })}

      {operator && rhs !== undefined && (
        <>
          <span className="mx-2 text-slate-400">{operator}</span>
          <span>{rhs}</span>
        </>
      )}
    </div>
  );
};

const MathPanel: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { currentProblem } = useGameStore(state => state.game);
  const { panels } = useGameStore(state => state.ui);
  const togglePanel = useGameStore(state => state.togglePanel);
  const isExpanded = panels.math === 'expanded';

  if (!currentProblem) return null;

  // Prefer rational data (exact Fractions) for display if available.
  const fracData = (currentProblem as any).fracData as RationalProblem | undefined;

  // Parse Objective
  const objectiveTerms = (() => {
    if (fracData) {
      return (fracData.c || []).map((cf, i) => {
        if (cf.isZero()) return null;
        const v = ['x', 'y', 'z'][i] || `x${i + 1}`;
        return {
          coeff: cf.toNumber(),
          varName: v,
          // Use the absolute value for display so that the sign
          // is controlled exclusively by SimpleMathFormula. This
          // avoids rendering things like "--1/4x" when the
          // underlying fraction is negative.
          displayCoeff: formatFractionForDisplay(cf.abs()),
        };
      }).filter(t => t !== null) as Array<{ coeff: number, varName: string, displayCoeff?: string }>;
    }
    return (currentProblem.c || []).map((coeff, i) => {
      if (coeff === 0) return null;
      const v = ['x', 'y', 'z'][i] || `x${i + 1}`;
      return { coeff, varName: v };
    }).filter(t => t !== null) as Array<{ coeff: number, varName: string, displayCoeff?: string }>;
  })();

  const Content = (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-xs font-bold text-slate-300 mb-1 tracking-wider">Objective</div>
        <SimpleMathFormula terms={objectiveTerms} prefix="max" />
      </div>

      <div>
        <div className="text-xs font-bold text-slate-300 mb-1 tracking-wider">Constraints</div>
        <div className="flex flex-col gap-1">
          {(fracData
            ? fracData.inequalities.map(ineq => ({
                alphaNum: ineq.alpha.map(a => a.toNumber()),
                // As with the objective, take the absolute value for
                // coefficient display so that SimpleMathFormula's sign
                // logic is the single source of truth for "+" / "-".
                alphaStr: ineq.alpha.map(a => formatFractionForDisplay(a.abs())),
                betaNum: ineq.beta.toNumber(),
                betaStr: formatFractionForDisplay(ineq.beta),
              }))
            : (currentProblem.inequalities || []).map(ineq => ({
                alphaNum: ineq.alpha,
                alphaStr: ineq.alpha.map(a => a.toString()),
                betaNum: ineq.beta,
                betaStr: ineq.beta.toString(),
              }))
          ).filter(ineq => {
            // Filter out non-negativity constraints x,y,z >= 0 using numeric view.
            const nonZero = ineq.alphaNum.filter(c => c !== 0);
            const isNonNeg = nonZero.length === 1 && ineq.alphaNum.some(c => c === -1) && ineq.betaNum === 0;
            return !isNonNeg;
          }).map((ineq, i) => {
            const terms = ineq.alphaNum.map((coeff, idx) => {
              if (coeff === 0) return null;
              const v = ['x', 'y', 'z'][idx] || `x${idx + 1}`;
              const displayCoeff = ineq.alphaStr[idx];
              return { coeff, varName: v, displayCoeff };
            }).filter(t => t !== null) as Array<{ coeff: number, varName: string, displayCoeff?: string }>;

            return (
              <SimpleMathFormula
                key={i}
                terms={terms}
                operator="≤"
                rhs={fracData ? ineq.betaStr : ineq.betaNum}
              />
            );
          })}
          <div className="text-slate-400 text-sm font-mono mt-1 pl-2">x, y, z ≥ 0</div>
        </div>
      </div>

      <button
        onClick={() => customProblemEditor.show(currentProblem)}
        className="w-full flex items-center justify-center gap-2 py-2 bg-slate-800 hover:bg-slate-700 border border-white/5 rounded-lg text-xs font-bold text-slate-300 transition-colors"
      >
        <Settings size={12} />
        Customize Problem
      </button>
    </div>
  );

  if (embedded) return Content;

  return (
    <div className={`pointer-events-auto absolute left-4 top-20 z-20 transition-all duration-300 ${isExpanded ? 'w-72' : 'w-auto'}`}>
      <div className="bg-slate-900/90 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div
          className="flex items-center justify-between p-3 bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
          onClick={() => togglePanel('math')}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold tracking-wider text-slate-400">Problem</span>
          </div>
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>

        {/* Content */}
        {isExpanded && (
          <div className="p-4">
            {Content}
          </div>
        )}
      </div>
    </div>
  );
};

export default MathPanel;
